/**
 * Gate any user action behind authentication.
 *
 *   const guardedUpload = useAnonymousGate(handleUpload);
 *   <button onClick={guardedUpload}>...</button>
 *
 * - Signed-in user → the wrapped action runs as usual.
 * - Anonymous user → the AuthModal opens in signup mode and the action is
 *   deferred. The flow's own state is left intact; after the user signs up,
 *   the AuthModal closes itself and the next click on the same button will
 *   succeed (so the user repeats the gesture once, which is the standard
 *   "soft paywall" UX). We don't auto-fire the action post-auth because
 *   most flows want the user to explicitly confirm after the page redraws
 *   with their new credit balance.
 */

import { useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthModal } from "@/contexts/AuthModalContext";

export function useAnonymousGate<TArgs extends unknown[]>(
  action: (...args: TArgs) => void | Promise<void>,
): (...args: TArgs) => void {
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();

  return useCallback(
    (...args: TArgs) => {
      if (!user) {
        openAuthModal("signup");
        return;
      }
      void action(...args);
    },
    [user, openAuthModal, action],
  );
}
